import React, { useEffect, useMemo, useState } from 'react';

import { buttons, colors, typography } from '@pray/shared/components/foundations';
import Button from '@pray/shared/components/ui/Button/Button';
import Text from '@pray/shared/components/ui/Text/Text';
import { PRAY_EXTERNAL_LINKS } from '@pray/shared/constants';
import { isOnBrowser } from '@pray/shared/utils';
import { isCookieBannerDismissed, shouldShowCookieBanner, updateCookieBannerAcceptance } from '@pray/shared/utils/gdpr';

import styles from './CookieBanner.module.scss';

/** List of route patterns where cookie banner should not be shown */
const excludedRoutes = [
  /^\/email-onboarding\/?$/, // e.g. /email-onboarding
  /^\/onboarding\/?$/, // e.g. /onboarding
  /^\/newsletter\/?/, // e.g. /newsletter/*
  /\/give\/?/, // e.g. /give, /:artist_handle/give, /give/:artist_handle
  /^\/chat\/?$/, // /chat — embedded widget iframe, no tracking needed
  /^\/drop-[a-z-]+\/signup\/?$/, // Drop campaign signup funnels — banner stays on the lander only
];

function isExcludedRoute() {
  if (typeof window === 'undefined') return false;
  return excludedRoutes.some((regex) => regex.test(window.location.pathname));
}

const CookieBanner = () => {
  const [isCookieBannerShown, setIsCookieBannerShown] = useState(false);

  useEffect(() => {
    if (shouldShowCookieBanner()) {
      const _isCookieBannerShown = !isExcludedRoute() && !isCookieBannerDismissed();
      setIsCookieBannerShown(_isCookieBannerShown);
    }
  }, []);

  async function onAcceptClick() {
    setIsCookieBannerShown(false);
    updateCookieBannerAcceptance(true);
  }

  function onRejectClick() {
    setIsCookieBannerShown(false);
    updateCookieBannerAcceptance(false);
  }

  const customAcceptClasses = useMemo(() => {
    const { pathname = '', hostname = '' } = isOnBrowser() ? window.location : {};

    if (/^(.+\.)?americaprays\.com$/.test(hostname) || /^\/americaprays\/?$/.test(pathname)) {
      return '!border-[#2975ea] bg-gradient-to-l from-[#f05c5c] to-[#2975ea] text-white';
    }

    return '';
  }, []);

  if (!isCookieBannerShown) return null;

  return (
    <div className={styles.cookieBanner}>
      <div className={styles.content}>
        <Text variant={typography.h2} className={styles.title}>
          We care about your privacy
        </Text>
        <Text variant={typography.body_small}>
          We use cookies and other tracking technologies to offer you a better experience, personalize content and ads,
          enable social media functionality and to analyze our performance and site traffic. By clicking
          &apos;Accept,&apos; you understand your information may be disclosed to our third-party partners. For more
          information, visit our{' '}
          <a className={styles.link} href={PRAY_EXTERNAL_LINKS.PRIVACY_POLICY} target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          .
        </Text>
        <div className={styles.buttons}>
          <Button
            variant={buttons.variant.primary}
            color={colors.pray_featured}
            className={customAcceptClasses}
            onClick={onAcceptClick}
          >
            Accept
          </Button>
          <Button variant={buttons.variant.secondary} onClick={onRejectClick}>
            Reject
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
